<template>
    <!-- ITDEVELOP-2722: the nested native confirm dialog is replaced by the
         shared ConfirmationModal (itself on ui/Dialog now). -->
    <ConfirmationModal
        v-if="showRemoveConfirm"
        title="Remove user"
        :question="`Are you sure you want to remove ${user?.FIRST_NAME ?? adminUserById?.first_name} ${user?.LAST_NAME ?? adminUserById?.last_name}?`"
        button-text="Remove"
        width-class="w-[28rem] max-w-[90vw]"
        @confirm="removeUser"
        @cancel="closeRemoveConfirm"
    />
    <div class="gap-md bg-lightest p-sm flex flex-col rounded-md">
        <div class="gap-xs flex flex-col">
            <p class="mb-xs font-bold">Reset password</p>
            <p>Send a password reset email to the user</p>
            <Button class="font-matter gap-sm flex font-normal" @click="resetPass"> Reset password </Button>
        </div>
        <template v-if="!onAdmin">
            <span class="divider"></span>
            <div class="gap-sm flex flex-col">
                <h3>{{ user?.ENABLED ? 'Lock User' : 'Unlock User' }}</h3>
                <p>
                    <template v-if="user?.ENABLED">
                        After the user is locked, all information will be unavailable to the account. The user will be
                        unable to log in. Existing connections to roles, groups, locations and requests will be saved,
                        but the user is unable to access them.
                    </template>
                    <template v-else>
                        Unlocking the user will restore their access to groups, locations, roles, and requests.
                    </template>
                </p>
                <Button
                    class="font-matter gap-sm font-normal"
                    :variant="user?.ENABLED ? 'destructive' : 'default'"
                    @click="toggleUser"
                >
                    {{ user?.ENABLED ? 'Lock' : 'Unlock' }} user
                </Button>
            </div>

            <span class="divider"></span>
            <div class="gap-sm flex flex-col">
                <h3>Remove user from organization</h3>
                <p>
                    Remove all asociation between the user and your organization. If the user is later invited to the
                    organization again your managers will have to setup relevant access rights
                </p>
                <Button variant="destructive" @click="openRemoveConfirm">Remove user</Button>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { useMutationAction, useOrganizationStore } from '@/stores/organization';
import type { AdminUserById, User } from '@/stores/store-interfaces';
import { computed, ref, type PropType } from 'vue';
import ConfirmationModal from '@/components/confirmation-modal.vue';

const organizationStore = useOrganizationStore();
const showRemoveConfirm = ref(false);

function openRemoveConfirm() {
    showRemoveConfirm.value = true;
}

function closeRemoveConfirm() {
    showRemoveConfirm.value = false;
}
const props = defineProps({
    user: {
        type: Object as PropType<User>,
        required: false,
    },
    adminUserById: {
        type: Object as PropType<AdminUserById>,
        required: false,
    },
});

const emit = defineEmits(['cancel', 'update']);
const onAdmin = computed(() => localStorage.getItem('admin') === 'true');
function resetPass() {
    const email = props.user?.EMAIL ?? props.adminUserById?.email;
    useAuthStore()
        .resetPass(email!)
        .then(() => {
            emit('cancel'); // close the modal
        });
}

async function toggleUser() {
    const userId = props.user?.ID ?? props.adminUserById?.user_id;
    if (!userId) return;
    await organizationStore
        .toggleUser(userId, !props.user?.ENABLED)
        .then((response) => {
            if (response.status !== 204) {
                organizationStore.setToastMessage('Failed to update user', 'error');
                return;
            } else {
                organizationStore.setToastMessage(`User ${props.user?.ENABLED ? 'locked' : 'unlocked'}`, 'check');
            }
            emit('update'); // close the modal
            emit('cancel'); // close the modal
        })
        .catch(() => {
            organizationStore.setToastMessage('Failed to update user', 'error');
        });
}

// async function removeUser() {
//     if (!props.user && !props.adminUserById) return;
//     await organizationStore
//         .deleteUser(props.user?.ID ?? props.adminUserById?.user_id!)
//         .then(() => {
//             organizationStore.setToastMessage('User deleted from organization', 'check');
//             router.push({ name: 'users' });
//         })
//         .catch(() => {
//             organizationStore.setToastMessage('Failed to delete user', 'warning');
//         });
// }

const runMutation = useMutationAction();

async function removeUser() {
    const id = props.user?.ID ?? props.adminUserById?.user_id;
    if (!id) return;
    await runMutation(() => organizationStore.deleteUser(id), {
        successMessage: 'User removed',
        errorMessage: `Couldn't remove user`,
        onSuccess: () => {
            router.push({ name: 'users' });
        },
    });
}
</script>
